/**
 * @fileoverview Delayed Effects Test Suite
 *
 * Tests for effects that expire after a certain number of turns.
 * These tests require multi-move sequences to verify the timing.
 *
 * Covers:
 * - Poison expiration (2 turns: poisoned piece skips, then can move)
 * - Zombification expiration (1 turn: enemy controls, then returns to owner)
 * - Shadow disappearance (shadows vanish after octopus's next turn)
 *
 * NOTE: These tests use a sequence of moves. The test framework will need
 * to execute each move in order, alternating between players.
 */

import { PieceType } from "../../zodiacEngine/types";
import { IntegrationTest, TestSuite } from "../types";

/**
 * Multi-move test sequence for delayed effects
 * Each step represents one move in the sequence
 */
export interface DelayedEffectTest {
  name: string;
  description: string;
  initialFEN: string;
  lightKing: PieceType;
  darkKing: PieceType;
  steps: {
    move: string;
    expectedFEN: string;
    expectedDelta?: {
      pieceMoved?: string;
      statusChanged?: boolean;
      turnChanged?: boolean;
      bonusTurn?: boolean;
      gameEnded?: boolean;
    };
    note?: string; // Human-readable explanation of what's happening
  }[];
}

// ============================================================================
// POISON EXPIRATION TESTS
// Poison lasts 2 turns: poisoned piece skips its next turn, then is free
// ============================================================================

export const poisonExpirationTest: DelayedEffectTest = {
  name: "Poison expires after two turns",
  description: "Poisoned piece skips one turn, then poison wears off",
  initialFEN: "CK7/8/M7/1f6/7bk",
  lightKing: PieceType.Crane,
  darkKing: PieceType.Bear,
  steps: [
    {
      move: "Light Monkey C1->D2:capture",
      expectedFEN: "CK7/8/8/1M!6/7bk",
      expectedDelta: {
        pieceMoved: "Light Monkey captured Dark Frog [C1 -> D2] Light Monkey is now poisoned",
        statusChanged: true,
        turnChanged: true,
      },
      note: "Monkey captures Frog and becomes poisoned",
    },
    {
      move: "Dark Bear E8->E7",
      expectedFEN: "CK7/8/8/1M!6/6bk1",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [E8 -> E7]",
        turnChanged: true,
      },
      note: "Dark makes a move, Light Monkey still poisoned",
    },
    {
      move: "Light Crane A1->A2",
      expectedFEN: "8/CK6/8/1M!5b/8k",
      expectedDelta: {
        pieceMoved: "Light Crane moved [A1 -> A2]",
        turnChanged: true,
      },
      note: "Light must move another piece (Monkey is poisoned/skips)",
    },
    {
      move: "Dark Bear E2->E3",
      expectedFEN: "8/CK6/8/1M6b/8k",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [E2 -> E3]",
        statusChanged: true, // Poison should expire
        turnChanged: true,
      },
      note: "Dark moves, poison wears off from Monkey",
    },
    {
      move: "Light Monkey D2->D3",
      expectedFEN: "8/CK6/1M6b/8/8k",
      expectedDelta: {
        pieceMoved: "Light Monkey moved [D2 -> D3]",
        turnChanged: true,
      },
      note: "Monkey is now free to move!",
    },
  ],
};

// ============================================================================
// ZOMBIFICATION EXPIRATION TESTS
// Zombified piece is controlled by opponent for 1 turn, then returns
// ============================================================================

export const zombificationExpirationTest: DelayedEffectTest = {
  name: "Zombification expires after one turn",
  description: "Zombified piece is controlled by enemy, then returns to owner",
  initialFEN: "CK7/8/1B6/1u6/7ck",
  lightKing: PieceType.Crane,
  darkKing: PieceType.Crane,
  steps: [
    {
      move: "Light Bear C2->D2:capture",
      expectedFEN: "CK7/8/8/1Bz6/7ck",
      expectedDelta: {
        pieceMoved: "Light Bear captured Dark Fungi [C2 -> D2] Light Bear is now zombified",
        statusChanged: true,
        turnChanged: true,
      },
      note: "Bear captures Fungi and becomes zombified",
    },
    {
      move: "Dark Bear D2->C2",
      expectedFEN: "CK7/8/1Bz6/8/7ck",
      expectedDelta: {
        pieceMoved: "Zombified Light Bear moved [D2 -> C2]",
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Dark player controls the zombified Bear (moves Light bear as dark player)",
    },
    {
      move: "Light Crane A1->A2",
      expectedFEN: "8/CK6/1B6/8/7ck",
      expectedDelta: {
        pieceMoved: "Light Crane moved [A1 -> A2]",
        statusChanged: true, // Zombification expires
        turnChanged: true,
      },
      note: "Light moves, zombification wears off from Bear",
    },
  ],
};

// ============================================================================
// SHADOW EXPIRATION TESTS
// Octopus shadows disappear after the octopus's next move
// ============================================================================

export const shadowExpirationTest: DelayedEffectTest = {
  name: "Shadows disappear after octopus moves",
  description: "Octopus creates shadows with ink, they disappear on next octopus move",
  initialFEN: "8/8/OK7/8/7bk",
  lightKing: PieceType.Octopus,
  darkKing: PieceType.Bear,
  steps: [
    {
      move: "Light Octopus C1->C2:ink",
      expectedFEN: "8/OK7/O1O5/O7/7bk",
      expectedDelta: {
        pieceMoved: "Light Octopus used ink on [C1]",
        turnChanged: true,
      },
      note: "Octopus inks, creating shadows at orthogonal empty squares",
    },
    {
      move: "Dark Bear E1->E2",
      expectedFEN: "8/OK7/O1O4b/O7/8k",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [E1 -> E2]",
        turnChanged: true,
      },
      note: "Dark moves, shadows still exist",
    },
    {
      move: "Light Octopus C2->C3",
      expectedFEN: "8/8/1OK6/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Octopus moved [C2 -> C3]",
        statusChanged: true, // Shadows disappear
        turnChanged: true,
      },
      note: "Octopus moves again, all previous shadows disappear",
    },
  ],
};

// ============================================================================
// MULTI-STEP SEQUENCE TESTS
// These test the full lifecycle of delayed effects
// ============================================================================

export const delayedEffectTests: IntegrationTest[] = [
  // Poison expiration sequence
  {
    name: "Poison expires after skip turn",
    description: "Poisoned piece skips one turn, then poison wears off",
    inputFEN: "CK7/8/M7/1f6/7bk",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Bear,
    move: "Light Monkey C1->D2:capture", // This is just for compatibility
    expectedFEN: "8/CK6/1M6b/8/8k", // Final FEN after all steps
    isSequence: true,
    steps: [
      {
        move: "Light Monkey C1->D2:capture",
        expectedFEN: "CK7/8/8/1M!6/7bk",
        expectedDelta: {
          pieceMoved: "Light Monkey captured Dark Frog [C1 -> D2] Light Monkey is now poisoned",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: ["Light Monkey: Poisoned"],
        note: "Monkey captures Frog and becomes poisoned",
      },
      {
        move: "Dark Bear E8->E7",
        expectedFEN: "CK7/8/8/1M!6/6bk1",
        expectedDelta: {
          pieceMoved: "Dark Bear moved [E8 -> E7]",
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
          statusChanged: false,
        },
        expectedStatus: ["Light Monkey: Poisoned"],
        note: "Dark makes a move, Light Monkey still poisoned",
      },
      {
        move: "Light Crane A1->A2",
        expectedFEN: "1CK6/8/8/1M6/6bk1",
        expectedDelta: {
          pieceMoved: "Light Crane moved [A1 -> A2]",
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
          statusChanged: false,
        },
        expectedStatus: ["Light Monkey: Poisoned"],
        note: "Light moves another piece (Monkey skips due to poison), poison still active",
      },
      {
        move: "Dark Bear E7->E8",
        expectedFEN: "1CK6/8/8/1M6/7bk",
        expectedDelta: {
          pieceMoved: "Dark Bear moved [E7 -> E8]",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Dark moves, poison clears from Monkey",
      },
      {
        move: "Light Monkey D2->D3",
        expectedFEN: "1CK6/8/8/2M5/7bk",
        expectedDelta: {
          pieceMoved: "Light Monkey moved [D2 -> D3]",
          turnChanged: true,
          statusChanged: false,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Monkey is now free to move!",
      },
    ],
  },

  // Zombification expiration sequence
  {
    name: "Zombification expires after one turn",
    description: "Zombified piece is controlled by enemy, then returns to owner",
    inputFEN: "CK7/8/1B6/1u6/7ck",
    lightKing: PieceType.Crane,
    darkKing: PieceType.Crane,
    move: "Light Bear C2->D2:capture",
    expectedFEN: "8/CK6/8/1B5c/8k",
    isSequence: true,
    steps: [
      {
        move: "Light Bear C2->D2:capture",
        expectedFEN: "CK7/8/8/1Bz6/7ck",
        expectedDelta: {
          pieceMoved: "Light Bear captured Dark Fungi [C2 -> D2] Light Bear is now zombified",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: ["Light Bear: Zombified"],
        note: "Bear captures Fungi and becomes zombified",
      },
      {
        move: "Dark Bear D2->C2",
        expectedFEN: "CK7/8/1B6/8/7ck",
        expectedDelta: {
          pieceMoved: "Zombified Light Bear moved [D2 -> C2]",
          statusChanged: true,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Dark player controls the zombified Bear",
      },
      {
        move: "Light Bear C2->D2",
        expectedFEN: "CK7/8/8/1B6/7ck",
        expectedDelta: {
          pieceMoved: "Light Bear moved [C2 -> D2]",
          statusChanged: false,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Light moves, zombification wears off from Bear",
      },
    ],
  },

  // Shadow expiration sequence
  // Note: Shadows disappear when ANY player moves after the ink (not just when octopus moves again)
  {
    name: "Shadows disappear after octopus moves",
    description: "Octopus creates shadows with ink, they disappear on next octopus move",
    inputFEN: "8/8/OK7/8/7bk",
    lightKing: PieceType.Octopus,
    darkKing: PieceType.Bear,
    move: "Light Octopus C1->C2:ink",
    expectedFEN: "8/8/1OK6/8/7bk",
    isSequence: true,
    steps: [
      {
        move: "Light Octopus C1->C2:ink",
        expectedFEN: "8/O7/1OK6/O7/7bk",
        expectedDelta: {
          pieceMoved: "Light Octopus used ink on [C1]",
          turnChanged: true,
          statusChanged: false,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [
          "Light Octopus at B1: isShadow=true (fake shadow)",
          "Light Octopus at D1: isShadow=true (fake shadow)",
          "Light Octopus at C2: isShadowed=true (real octopus, inked)",
        ],
        note: "Octopus inks, creating shadows at B1 and D1, real octopus at C2 is shadowed",
      },
      {
        move: "Dark Bear E8->E7",
        expectedFEN: "8/8/1OK6/8/6bk1",
        expectedDelta: {
          pieceMoved: "Dark Bear moved [E8 -> E7]",
          turnChanged: true,
          statusChanged: false,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Dark moves, shadows and isShadowed cleared after opponent's move",
      },
      {
        move: "Light Octopus C2->C3",
        expectedFEN: "8/8/2OK5/8/6bk1",
        expectedDelta: {
          pieceMoved: "Light Octopus moved [C2 -> C3]",
          statusChanged: false,
          turnChanged: true,
          bonusTurn: false,
          gameEnded: false,
        },
        expectedStatus: [],
        note: "Octopus moves normally, no shadow effects",
      },
    ],
  },
];

export const delayedEffectsSuite: TestSuite = {
  name: "Delayed Effects",
  description: "Tests for effects that expire after turns (Poison, Zombify, Shadows)",
  tests: delayedEffectTests,
};

// Export multi-move sequences separately for future test runner support
export const delayedEffectSequences: DelayedEffectTest[] = [
  poisonExpirationTest,
  zombificationExpirationTest,
  shadowExpirationTest,
];
